import { useEffect, useMemo, useState } from "react"
import { supabase } from "../../supabaseClient"

type EditSessionModalProps = {
  isOpen: boolean
  sessionId: string | null
  onClose: () => void
  onSaved: () => void
}

type BreakRow = {
  start_time: string
  end_time: string | null
}

function toLocalInputValue(iso: string) {
  // input[type=datetime-local] expects "YYYY-MM-DDTHH:mm"
  const d = new Date(iso)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  const hh = String(d.getHours()).padStart(2, "0")
  const mi = String(d.getMinutes()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

function localInputToIso(localValue: string) {
  // interpret as local time and convert to ISO
  const d = new Date(localValue)
  return d.toISOString()
}

export default function EditSessionModal({
  isOpen,
  sessionId,
  onClose,
  onSaved
}: EditSessionModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [startLocal, setStartLocal] = useState("")
  const [endLocal, setEndLocal] = useState("")

  const [breaks, setBreaks] = useState<BreakRow[]>([])

  useEffect(() => {
    if (!isOpen || !sessionId) return

    const load = async () => {
      setLoading(true)
      setError(null)

      const { data: session, error: sErr } = await supabase
        .from("work_sessions")
        .select("id, start_time, end_time, status")
        .eq("id", sessionId)
        .single()

      if (sErr || !session) {
        setError(sErr?.message || "Session not found")
        setLoading(false)
        return
      }

      if (!session.end_time) {
        setError("This session has no end time. Only completed sessions can be edited.")
        setLoading(false)
        return
      }

      setStartLocal(toLocalInputValue(session.start_time))
      setEndLocal(toLocalInputValue(session.end_time))

      const { data: b, error: bErr } = await supabase
        .from("work_breaks")
        .select("start_time, end_time")
        .eq("session_id", sessionId)

      if (bErr) {
        setError(bErr.message)
        setBreaks([])
      } else {
        setBreaks((b as BreakRow[]) || [])
      }

      setLoading(false)
    }

    load()
  }, [isOpen, sessionId])

  const preview = useMemo(() => {
    if (!startLocal || !endLocal) return null

    const start = new Date(startLocal)
    const end = new Date(endLocal)

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
    if (end.getTime() <= start.getTime()) {
      return { ok: false, message: "End time must be after start time." }
    }

    // Sum closed breaks (ignore open breaks, but completed sessions shouldn't have open breaks)
    let breakSeconds = 0
    for (const b of breaks) {
      if (!b.end_time) continue
      breakSeconds +=
        (new Date(b.end_time).getTime() - new Date(b.start_time).getTime()) / 1000
    }

    const totalSeconds = (end.getTime() - start.getTime()) / 1000
    const workedSeconds = Math.max(0, totalSeconds - breakSeconds)
    const workedMins = Math.floor(workedSeconds / 60)

    return {
      ok: true,
      breakMins: Math.floor(breakSeconds / 60),
      workedMins
    }
  }, [startLocal, endLocal, breaks])

  const handleSave = async () => {
    if (!sessionId) return
    if (!preview || !preview.ok) return

    setLoading(true)
    setError(null)

    const startIso = localInputToIso(startLocal)
    const endIso = localInputToIso(endLocal)

    const { error: uErr } = await supabase
      .from("work_sessions")
      .update({
        start_time: startIso,
        end_time: endIso,
        duration_mins: preview.workedMins,
        is_manually_edited: true,
        updated_at: new Date().toISOString()
      })
      .eq("id", sessionId)

    if (uErr) {
      setError(uErr.message)
      setLoading(false)
      return
    }

    setLoading(false)
    onSaved()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-neutral-900 shadow-xl border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-black dark:text-white">Edit session</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Adjust start/end times. Duration will be recalculated excluding breaks.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}

          <div className="space-y-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Start time</label>
            <input
              type="datetime-local"
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">End time</label>
            <input
              type="datetime-local"
              value={endLocal}
              onChange={(e) => setEndLocal(e.target.value)}
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-800 px-3 py-2.5 text-sm text-black dark:text-white focus:outline-none transition"
              disabled={loading}
            />
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-gray-50 dark:bg-neutral-800">
            {!preview ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Enter valid start and end times to preview changes.</p>
            ) : !preview.ok ? (
              <p className="text-sm text-red-600">{preview.message}</p>
            ) : (
              <div className="flex items-center justify-between text-sm">
                <div className="text-gray-600 dark:text-gray-400">
                  Breaks: <span className="font-medium text-black dark:text-white">{preview.breakMins} min</span>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  New worked time: <span className="font-medium text-black dark:text-white">{preview.workedMins} min</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="border border-gray-200 dark:border-gray-700 px-4 py-2 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800 transition"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            className="bg-[#2563EB] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#1d4ed8] transition disabled:opacity-50"
            disabled={loading || !preview || !preview.ok}
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  )
}
